import { buildContactRelationJsonLd, getContactSeo } from '../components/contact-cards/contactSeoConfig';
import { getMenuSeo } from '../components/menu/menuSeoConfig';
import {
  buildPortfolioCaseStudyJsonLd,
  buildPortfolioItemListJsonLd,
  getPortfolioCaseStudyMeta,
  getPortfolioItemBySlug,
  PORTFOLIO_CLIENT_NAMES,
  PORTFOLIO_META_DESCRIPTION,
} from '../data/portfolioItems';
import { getSiteSeo } from './siteSeoConfig';
import {
  DEFAULT_DESCRIPTION,
  ELYPTEK_ORGANIZATION,
  ELYPTEK_PROFESSIONAL_SERVICE,
  ELYPTEK_WEBSITE,
  OG_IMAGE,
  SITE,
  SITE_NAME,
} from './siteConstants';

const normalizePath = (pathname) => decodeURIComponent(pathname).replace(/\/+$/, '') || '/';

const matchPortfolioCaseStudy = (path) => {
  const match = path.match(/^\/portfolio\/([^/]+)$/);
  if (!match) return null;
  return getPortfolioItemBySlug(match[1]);
};

const webPageNode = (pageUrl, title, description) => ({
  '@type': 'WebPage',
  '@id': `${pageUrl}#webpage`,
  url: pageUrl,
  name: title,
  description,
  inLanguage: ['en', 'ar'],
  isPartOf: { '@id': `${SITE}/#website` },
});

export const buildPageSeoPayload = (pathname) => {
  const path = normalizePath(pathname);
  const pageUrl = `${SITE}${path}`;

  const contact = getContactSeo(path);
  if (contact) {
    const title = `${contact.nameEn} | Network Contact | ${SITE_NAME}`;
    const description = contact.descriptionEn;
    const { profilePage, entity } = buildContactRelationJsonLd(pageUrl, contact);

    return {
      title,
      description,
      canonicalUrl: pageUrl,
      imageUrl: OG_IMAGE,
      jsonLd: {
        '@context': 'https://schema.org',
        '@graph': [
          profilePage,
          entity,
          {
            ...ELYPTEK_ORGANIZATION,
            knowsAbout: contact.nameEn,
          },
          ELYPTEK_WEBSITE,
        ],
      },
    };
  }

  const menu = getMenuSeo(path);
  if (menu) {
    const title = `${menu.nameEn} | Menu | ${SITE_NAME}`;
    const description = menu.descriptionEn;
    const imageUrl = menu.logo ? `${SITE}${menu.logo}` : OG_IMAGE;
    return {
      title,
      description,
      canonicalUrl: pageUrl,
      imageUrl,
      jsonLd: {
        '@context': 'https://schema.org',
        '@graph': [
          webPageNode(pageUrl, title, description),
          {
            '@type': 'Restaurant',
            '@id': `${pageUrl}#restaurant`,
            name: menu.nameEn,
            alternateName: menu.nameAr,
            url: pageUrl,
            image: imageUrl,
            hasMenu: {
              '@type': 'Menu',
              '@id': `${pageUrl}#menu`,
              name: `${menu.nameEn} Menu`,
              url: pageUrl,
              inLanguage: ['en', 'ar'],
            },
          },
          ELYPTEK_ORGANIZATION,
          ELYPTEK_WEBSITE,
        ],
      },
    };
  }

  const portfolioItem = matchPortfolioCaseStudy(path);
  if (portfolioItem) {
    const { title, description } = getPortfolioCaseStudyMeta(portfolioItem);
    const imageUrl = `${SITE}${encodeURI(portfolioItem.image)}`;
    return {
      title,
      description,
      canonicalUrl: pageUrl,
      imageUrl,
      jsonLd: {
        '@context': 'https://schema.org',
        '@graph': [
          {
            ...webPageNode(pageUrl, title, description),
            about: { '@id': `${pageUrl}#project` },
            mainEntity: { '@id': `${pageUrl}#project` },
          },
          buildPortfolioCaseStudyJsonLd(portfolioItem, pageUrl),
          {
            ...ELYPTEK_ORGANIZATION,
            knowsAbout: [
              portfolioItem.title,
              'Elyptek portfolio projects',
              'websites designed and developed by Elyptek',
            ],
          },
          ELYPTEK_WEBSITE,
        ],
      },
    };
  }

  const site = getSiteSeo(path);
  if (site) {
    if (site.jsonLdType === 'home') {
      return {
        title: site.title,
        description: site.description,
        canonicalUrl: pageUrl,
        imageUrl: OG_IMAGE,
        jsonLd: {
          '@context': 'https://schema.org',
          '@graph': [
            ELYPTEK_WEBSITE,
            ELYPTEK_ORGANIZATION,
            {
              ...webPageNode(pageUrl, site.title, site.description),
              about: { '@id': `${SITE}/#organization` },
            },
            {
              ...ELYPTEK_PROFESSIONAL_SERVICE,
              description: site.description,
            },
          ],
        },
      };
    }

    if (path === '/portfolio') {
      const description = PORTFOLIO_META_DESCRIPTION;
      const title = site.title;
      return {
        title,
        description,
        canonicalUrl: pageUrl,
        imageUrl: OG_IMAGE,
        jsonLd: {
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'CollectionPage',
              '@id': `${pageUrl}#webpage`,
              url: pageUrl,
              name: title,
              description,
              inLanguage: ['en', 'ar'],
              isPartOf: { '@id': `${SITE}/#website` },
              about: { '@id': `${SITE}/#organization` },
              mainEntity: { '@id': `${pageUrl}#itemlist` },
            },
            buildPortfolioItemListJsonLd(pageUrl),
            {
              ...ELYPTEK_ORGANIZATION,
              knowsAbout: [
                ...PORTFOLIO_CLIENT_NAMES,
                'Elyptek projects',
                'Elyptek clients',
                'websites designed and developed by Elyptek',
              ],
            },
            ELYPTEK_WEBSITE,
          ],
        },
      };
    }

    return {
      title: site.title,
      description: site.description,
      canonicalUrl: pageUrl,
      imageUrl: OG_IMAGE,
      jsonLd: {
        '@context': 'https://schema.org',
        '@graph': [
          webPageNode(pageUrl, site.title, site.description),
          ELYPTEK_ORGANIZATION,
          ELYPTEK_WEBSITE,
        ],
      },
    };
  }

  return {
    title: SITE_NAME,
    description: DEFAULT_DESCRIPTION,
    canonicalUrl: pageUrl,
    imageUrl: OG_IMAGE,
    jsonLd: null,
  };
};
